import { useEffect, useState, useCallback } from "react";
import { opApi, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Eye, RotateCcw, ExternalLink, Pencil, Copy, Plus, Trash2 } from "lucide-react";
import { VerticalEditor } from "@/components/mc/vb/VerticalEditor";
import { STATUS, tabName } from "@/components/mc/vb/vbUtils";

const STATUS_STYLE = { live: "border-primary text-primary", coming_soon: "border-border text-muted-foreground", draft: "border-amber-400 text-amber-700 bg-amber-50" };

function VerticalCard({ v, busy, onOpen, onReset, onEdit, onDuplicate, onDelete }) {
  const cfg = v.config;
  return (
    <div className="border-r border-b border-border p-5 flex flex-col gap-3" data-testid={`vertical-card-${v.key}`}>
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-display font-bold text-lg">{v.label}</h3>
        <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border ${STATUS_STYLE[v.status]}`} data-testid={`vertical-status-${v.key}`}>{STATUS[v.status]}</span>
      </div>
      <p className="text-sm text-muted-foreground">{v.blurb}</p>
      <div className="text-xs space-y-1" data-testid={`vertical-config-${v.key}`}>
        <div className="text-muted-foreground">{v.product === "morrconnect" ? "MorrConnect" : "MorrCart"} · {v.shops} shop{v.shops !== 1 ? "s" : ""}</div>
        {cfg && <div className="flex flex-wrap gap-1">{cfg.tabs.map((t) => <span key={t} className="border border-border px-1.5 py-0.5">{tabName(cfg, t)}</span>)}</div>}
        {cfg && <div><span className="text-muted-foreground">Storefront button:</span> {cfg.storefront?.cta}</div>}
      </div>
      <div className="flex flex-wrap gap-2 mt-auto pt-2">
        <Button size="sm" variant="outline" className="rounded-none gap-1.5" onClick={() => onEdit(v)} data-testid={`vertical-edit-${v.key}`}><Pencil className="h-3.5 w-3.5" /> Edit</Button>
        <Button size="sm" variant="outline" className="rounded-none gap-1.5" disabled={busy} onClick={() => onDuplicate(v)} data-testid={`vertical-duplicate-${v.key}`}><Copy className="h-3.5 w-3.5" /> Duplicate</Button>
        <Button size="sm" className="rounded-none gap-1.5" disabled={busy} onClick={() => onOpen(v)} data-testid={`vertical-open-${v.key}`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />} Open preview
        </Button>
        {v.status === "draft" && !v.builtin && !v.shops && (
          <Button size="sm" variant="ghost" className="rounded-none gap-1.5 text-destructive" disabled={busy} onClick={() => onDelete(v)} data-testid={`vertical-delete-${v.key}`}><Trash2 className="h-3.5 w-3.5" /> Delete</Button>
        )}
        {v.sandbox && <>
          <Button size="sm" variant="outline" className="rounded-none gap-1.5" disabled={busy} onClick={() => onReset(v)} data-testid={`vertical-reset-${v.key}`}><RotateCcw className="h-3.5 w-3.5" /> Reset sample data</Button>
          <a href={v.sandbox.public_path} target="_blank" rel="noreferrer" data-testid={`vertical-public-${v.key}`} className="inline-flex items-center gap-1 text-sm text-primary hover:underline px-2">
            <ExternalLink className="h-3.5 w-3.5" /> Public page
          </a>
        </>}
      </div>
    </div>
  );
}

export function VerticalsPanel({ onSwitch }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [editing, setEditing] = useState(undefined); // undefined = list, null = new, key = edit
  const load = useCallback(() => opApi.get("/admin/verticals").then(({ data: d }) => setData(d)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const run = async (key, reset) => {
    setBusy(key);
    try {
      const { data: sb } = await opApi.post(`/admin/verticals/${key}/sandbox${reset ? "?reset=true" : ""}`);
      if (reset) { toast.success("Sample data reset"); await load(); }
      else await onSwitch(sb);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  const duplicate = async (v) => {
    setBusy(v.key);
    try { const { data: d } = await opApi.post(`/admin/verticals/${v.key}/duplicate`); toast.success(`Created ${d.label}`); await load(); setEditing(d.key); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Delete the draft "${v.label}" and its sandbox shop?`)) return;
    setBusy(v.key);
    try { await opApi.delete(`/admin/verticals/${v.key}`); toast.success("Draft deleted"); await load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  if (!data) return <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (editing !== undefined) {
    return <VerticalEditor key={editing || "new"} vkey={editing} meta={data} onPreview={(k) => run(k, false)}
      onClose={(k) => { load(); setEditing(k || undefined); }} />;
  }
  return (
    <div data-testid="verticals-panel">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display font-bold text-xl">Verticals</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">Build and edit verticals from existing modules. Each one gets a hidden sandbox shop with sample data. Drafts are only visible to you; Coming soon shows locked on the wall; Live can be picked by owners.</p>
        </div>
        <Button className="rounded-none gap-1.5" onClick={() => setEditing(null)} data-testid="vertical-new"><Plus className="h-4 w-4" /> New vertical</Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-border">
        {data.verticals.map((v) => <VerticalCard key={v.key} v={v} busy={busy === v.key} onOpen={(x) => run(x.key, false)} onReset={(x) => run(x.key, true)}
          onEdit={(x) => setEditing(x.key)} onDuplicate={duplicate} onDelete={remove} />)}
      </div>
    </div>
  );
}
